/**
 * Global Cart Sorting Manager
 * Handles automatic sorting of cart items by price (high to low)
 */
class CartSorter {
  constructor() {
    this.sortingQueue = [];
    this.isSorting = false;
    this.debouncedSort = this.debounce(this.processSortingQueue.bind(this), 300);
    this.isEnabled = true; // Can be toggled if needed
    this.cartStateLock = false; // Prevent other cart operations during sorting
  }

  /**
   * Debounce function to limit rapid executions
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Enable or disable cart sorting
   * @param {boolean} enabled - Whether to enable cart sorting
   */
  setEnabled(enabled) {
    this.isEnabled = enabled;
  }

  /**
   * Check if cart is currently locked (during sorting)
   * @returns {boolean} True if cart is locked
   */
  isCartLocked() {
    return this.cartStateLock;
  }

  /**
   * Intercept and block cart API calls during sorting
   */
  interceptCartAPIs() {
    try {
      // Store original fetch
      if (!this.originalFetch) {
        this.originalFetch = window.fetch;
      }

      // Override fetch to block cart operations
      window.fetch = async (url, options = {}) => {
        const isCartOperation = url.includes('/cart/add.js') || 
                               url.includes('/cart/update.js') || 
                               url.includes('/cart/change.js');

        if (isCartOperation && this.cartStateLock) {
          // Check if this is our own sorting operation (allow it)
          const isSortingOperation = options.body && 
            (options.body.includes('"sorting_operation":true') || 
             options.body.includes('sorting_operation'));
          
          if (isSortingOperation) {
            console.log('Allowing sorting operation:', url);
            return this.originalFetch(url, options);
          }

          console.log('Blocking external cart API call during sorting:', url);
          // Return a mock successful response to prevent errors
          return new Response(JSON.stringify({ 
            items: [], 
            item_count: 0, 
            total_price: 0,
            note: 'Cart operation blocked during sorting'
          }), {
            status: 200,
            headers: { 'Content-Type': 'application/json' }
          });
        }

        // Allow non-cart operations to proceed normally
        return this.originalFetch(url, options);
      };

      console.log('Cart API interception enabled');
    } catch (error) {
      console.warn('Error setting up cart API interception:', error);
    }
  }

  /**
   * Restore original fetch function
   */
  restoreCartAPIs() {
    try {
      if (this.originalFetch) {
        window.fetch = this.originalFetch;
        this.originalFetch = null;
        console.log('Cart API interception disabled');
      }
    } catch (error) {
      console.warn('Error restoring cart APIs:', error);
    }
  }

  /**
   * Queue cart sorting operation
   * @param {Array} addedItems - Items that were just added to cart
   */
  queueCartSorting(addedItems = []) {
    if (!this.isEnabled) {
      console.log('Cart sorting is disabled');
      return;
    }
    
    // Prevent duplicate sorting operations
    if (this.isSorting) {
      console.log('Cart sorting already in progress, skipping this request');
      return;
    }

    // Set sessionStorage flag for cart page redirects
    try {
      window.sessionStorage.setItem('pendingCartSort', '1');
    } catch (e) {
      console.warn('Could not set sessionStorage flag:', e);
    }

    console.log('Cart sorting queued with items:', addedItems);
    this.sortingQueue.push({ addedItems, timestamp: Date.now() });
    this.debouncedSort();
  }

  /**
   * Process the cart sorting queue
   */
  async processSortingQueue() {
    if (this.isSorting || this.sortingQueue.length === 0 || !this.isEnabled) return;
    this.isSorting = true;
    
    // Disable cart interactions during sorting
    this.disableCartInteractions();
    
    try {
      // Clear the queue and process all pending operations
      const queueItems = [...this.sortingQueue];
      this.sortingQueue = [];
      // Compute expected variant IDs that were just added
      const expectedVariantIds = new Set();
      try {
        queueItems.forEach(q => {
          (q.addedItems || []).forEach(it => {
            const vid = parseInt(it.id || it.variant_id);
            if (!Number.isNaN(vid)) expectedVariantIds.add(vid);
          });
        });
      } catch (e) {}

      await this.sortCartByPrice(expectedVariantIds);
    } catch (error) {
      console.error('Error sorting cart:', error);
    } finally {
      this.isSorting = false;
      
      // Re-enable cart interactions after sorting
      this.enableCartInteractions();
      
      // Process any new items that were queued while sorting
      if (this.sortingQueue.length > 0) {
        this.debouncedSort();
      }
    }
  }

  /**
   * Sort cart items by price in descending order
   * Uses total line price first, then unit price as tie-breaker
   */
  async sortCartByPrice(expectedVariantIds = new Set()) {
    try {
      // Optionally wait for '/cart.js' to reflect newly added items
      const cartData = await this._getFreshCartReflecting(expectedVariantIds);
      if (!cartData.items || cartData.items.length <= 1) {
        console.log('Cart has 1 or fewer items, no sorting needed');
        return; // No need to sort if cart is empty or has only one item
      }

      // Check if we have duplicate items (same variant_id multiple times)
      const variantCounts = new Map();
      cartData.items.forEach(item => {
        const variantId = item.variant_id || item.id;
        variantCounts.set(variantId, (variantCounts.get(variantId) || 0) + 1);
      });
      
      const hasDuplicates = Array.from(variantCounts.values()).some(count => count > 1);
      if (hasDuplicates) {
        console.log('Cart has duplicate items, proceeding with sort');
      } else {
        console.log('Cart has no duplicates, checking if sorting is needed');
      }

      // Sort by TOTAL line price (ascending). Tie-break by unit price (ascending)
      const getLineTotal = (item) => {
        if (typeof item.final_line_price === 'number') return item.final_line_price;
        if (typeof item.line_price === 'number') return item.line_price;
        const qty = typeof item.quantity === 'number' ? item.quantity : 1;
        return (item.price || 0) * qty;
      };
      const getUnitPrice = (item) => {
        if (typeof item.final_price === 'number') return item.final_price;
        return item.price || 0;
      };

      const sortedItems = [...cartData.items].sort((a, b) => {
        const totalDiff = getLineTotal(a) - getLineTotal(b);
        if (totalDiff !== 0) return totalDiff;
        return getUnitPrice(a) - getUnitPrice(b);
      });



      // Check if items are already in correct order

      const isAlreadySorted = cartData.items.every((item, index) => item.id === sortedItems[index].id);

      
      if (isAlreadySorted && cartData.item_count !== 2) {
        return; // Cart is already sorted correctly
      }

      // Clear cart and re-add items in sorted order
      await this.clearAndReorderCart(sortedItems);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetch cart and ensure it reflects any expected variant IDs (retry briefly)
   */
  async _getFreshCartReflecting(expectedVariantIds) {
    const hasAllExpected = (cart) => {
      if (!expectedVariantIds || expectedVariantIds.size === 0) return true;
      const present = new Set((cart.items || []).map(i => parseInt(i.variant_id || i.id)));
      for (const vid of expectedVariantIds) { if (!present.has(vid)) return false; }
      return true;
    };

    const attemptFetch = async () => {
      const resp = await fetch('/cart.js');
      if (!resp.ok) throw new Error(`HTTP error! status: ${resp.status}`);
      return await resp.json();
    };

    let tries = 0;
    const maxTries = 6; // ~900ms max wait
    while (tries < maxTries) {
      const cart = await attemptFetch();
      if (hasAllExpected(cart)) return cart;
      await this._sleep(150);
      tries += 1;
    }
    // Return last known cart if expectations not fully met
    return await attemptFetch();
  }

  _sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

  /**
   * Clear cart and re-add items in sorted order
   * @param {Array} sortedItems - Items sorted by price
   */
  async clearAndReorderCart(sortedItems) {
    try {
      // Clear the cart by updating all items to quantity 0
      await this.clearCart();
      // Re-add items in batches to avoid overwhelming the API
      await this.addItemsInBatches(sortedItems);
      
      // Verify cart has items after re-adding
      await this.verifyCartNotEmpty();
      
      // Update cart UI
      this.updateCartUI();
    } catch (error) {
      console.error('Error in clearAndReorderCart:', error);
      throw error;
    }
  }

  /**
   * Clear all items from cart
   */
  async clearCart() {
    try {

      
      // Preferred: clear entire cart via Shopify clear endpoint
      const clearResponse = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ sorting_operation: true }) // Flag to identify our sorting operations
      });
      
      if (clearResponse.ok) {
        try {
          return await clearResponse.json();
        } catch (_) {
          console.log('Clear response had no JSON body');
        }
      }
      
      // Fallback: use updates with variant IDs set to 0
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      const updates = {};
      
      if (cartData.items) {
        cartData.items.forEach((item) => {
          const variantId = item.variant_id || item.id;
          if (variantId) {
            updates[variantId] = 0;
            console.log(`Setting variant ${variantId} to quantity 0`);
          }
        });
      }
      

      
      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          updates,
          sorting_operation: true // Flag to identify our sorting operations
        })
      });
      
      if (!updateResponse.ok) {
        throw new Error(`HTTP error! status: ${updateResponse.status}`);
      }

      return await updateResponse.json();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Add items back to cart in batches
   * @param {Array} items - Items to add back to cart
   */
  async addItemsInBatches(items) {
    console.log('Re-adding items in batches:', items.length, 'total items');
    const BATCH_SIZE = 3; // Process 3 items at a time to avoid API limits
    
    for (let i = 0; i < items.length; i += BATCH_SIZE) {
      const batch = items.slice(i, i + BATCH_SIZE);
      const itemsToAdd = batch.map(item => ({
        id: item.variant_id || item.id,
        quantity: item.quantity,
        properties: item.properties || {}
      }));

      console.log(`Adding batch ${Math.floor(i / BATCH_SIZE) + 1}:`, itemsToAdd);
      
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ 
            items: itemsToAdd,
            sorting_operation: true // Flag to identify our sorting operations
          })
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        console.log(`Batch ${Math.floor(i / BATCH_SIZE) + 1} added successfully:`, result);
        
        // Small delay between batches to prevent overwhelming the server
        if (i + BATCH_SIZE < items.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      } catch (error) {
        console.error(`Error adding batch ${Math.floor(i / BATCH_SIZE) + 1}:`, error);
        throw error;
      }
    }

    console.log('All items re-added successfully');
  }

  /**
   * Verify cart has items after re-adding
   */
  async verifyCartNotEmpty() {
    try {
      console.log('Verifying cart has items...');
      
      // Wait a moment for cart to settle
      await this._sleep(500);
      
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      
      console.log('Cart verification - Item count:', cartData.item_count, 'Items:', cartData.items?.length);
      
      if (!cartData.items || cartData.items.length === 0) {
        console.error('Cart is empty after re-adding! Attempting to restore...');
        // This shouldn't happen, but if it does, we could try to restore
        throw new Error('Cart verification failed - cart is empty');
      }

      console.log('Cart verification successful - items present');
    } catch (error) {
      console.error('Cart verification error:', error);
      throw error;
    }
  }

  /**
   * Update cart UI after sorting
   */
  updateCartUI() {
    try {
      const cartElement = document.querySelector('cart-form');
      if (cartElement && typeof cartElement.getCartData === 'function') {
        cartElement.getCartData();
      }
      // Update global cart variable by fetching latest cart data
      this.updateGlobalCartVariable();
    } catch (error) {
      console.error('Error updating cart UI:', error);
    }
  }

  /**
   * Update the global cart variable
   */
  async updateGlobalCartVariable() {
    try {
      const cartResponse = await fetch('/cart.js');
      if (cartResponse.ok) {
        const cartData = await cartResponse.json();
        if (window.globalVariables) {
          window.globalVariables.cart = cartData;   
        }
      }
    } catch (error) {
      console.error('Error updating global cart variable:', error);
    }
  }

  /**
   * Disable cart interactions during sorting
   */
  disableCartInteractions() {
    try {
      // Lock cart state to prevent other operations
      this.cartStateLock = true;
      console.log('Cart state locked - preventing other cart operations');
      
      // Intercept and block cart API calls during sorting
      this.interceptCartAPIs();
      
      // Disable all cart buttons (safely)
      const cartButtons = document.querySelectorAll('[data-qty-btn], [data-item-remove], button[type="submit"][name="add"], .btn[href*="cart"], .checkout-btn, [data-checkoutBtns] button');
      cartButtons.forEach(btn => {
        if (btn) {
          btn.disabled = true;
          btn.classList.add('cart-sorting-disabled');
        }
      });

      // Add loading state to cart containers (safely)
      const cartContainers = document.querySelectorAll('cart-form, .cart-form-container, #cart-form');
      cartContainers.forEach(container => {
        if (container) {
          container.classList.add('cart-sorting');
          container.setAttribute('aria-busy', 'true');
        }
      });

      // Add subtle loading indicator to cart title/header (safely)
      const cartTitle = document.querySelector('.cart-title, .cart-header h2, .cart-header h1, .cart__title, .cart-title h1, .cart-title h2');
      if (cartTitle && !cartTitle.querySelector('.cart-sorting-indicator')) {
        try {
          const indicator = document.createElement('span');
          indicator.className = 'cart-sorting-indicator spinner-border spinner-border-sm ms-2';
          indicator.setAttribute('role', 'status');
          indicator.setAttribute('aria-hidden', 'true');
          indicator.style.cssText = 'width: 1rem; height: 1rem;';
          cartTitle.appendChild(indicator);
        } catch (indicatorError) {
          console.warn('Could not add loading indicator:', indicatorError);
        }
      }
    } catch (error) {
      console.warn('Error disabling cart interactions:', error);
    }
  }

  /**
   * Re-enable cart interactions after sorting
   */
  enableCartInteractions() {
    try {
      // Unlock cart state
      this.cartStateLock = false;
      console.log('Cart state unlocked - allowing other cart operations');
      
      // Restore original cart APIs
      this.restoreCartAPIs();
      
      // Re-enable all cart buttons (safely)
      const cartButtons = document.querySelectorAll('.cart-sorting-disabled');
      cartButtons.forEach(btn => {
        if (btn) {
          btn.disabled = false;
          btn.classList.remove('cart-sorting-disabled');
        }
      });

      // Remove loading state from cart containers (safely)
      const cartContainers = document.querySelectorAll('.cart-sorting');
      cartContainers.forEach(container => {
        if (container) {
          container.classList.remove('cart-sorting');
          container.removeAttribute('aria-busy');
        }
      });

      // Remove subtle loading indicator (safely)
      const indicator = document.querySelector('.cart-sorting-indicator');
      if (indicator) {
        try {
          indicator.remove();
        } catch (indicatorError) {
          console.warn('Could not remove loading indicator:', indicatorError);
        }
      }
    } catch (error) {
      console.warn('Error enabling cart interactions:', error);
    }
  }


}

// Create global instance
window.cartSorter = new CartSorter();

// Export for module usage if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CartSorter;
}


